import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatoBadge, MatchBadge, STATI } from "@/components/studio/common";
import { ClienteCard, MatchCard, ImportiCard } from "@/components/studio/pratica/InfoCards";
import { MandatoCard, DocumentiCard } from "@/components/studio/pratica/DocCards";
import { TimelineCard, NotesCard, RemindersCard, FlagsCard } from "@/components/studio/pratica/SideCards";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

export default function PraticaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);

  const load = useCallback(() => api.get(`/pratiche/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(formatErr(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/pratiche/${id}`, body);
      setP(data);
      toast.success("Pratica aggiornata");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!p) return <Loader2 className="h-8 w-8 animate-spin text-navy mx-auto mt-20" />;
  const ctx = { p, setP, load, patch, user };

  return (
    <div data-testid="pratica-detail">
      <Link to="/studio" data-testid="pratica-back-link" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" />Pratiche</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mt-3 mb-8">
        <div>
          <p className="font-mono text-xs text-slate-500" data-testid="pratica-numero">{p.numero}</p>
          <h1 className="font-serif text-4xl text-navy" data-testid="pratica-cliente-nome">{p.cliente.cognome} {p.cliente.nome}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <StatoBadge s={p.status} /><MatchBadge m={p.match} />
            {p.kyc_verificato && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">KYC ok</span>}
            {p.flags?.revoca && <span data-testid="flag-revoca-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">Revoca</span>}
            {p.flags?.bypass && <span data-testid="flag-bypass-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">By-pass</span>}
          </div>
        </div>
        <div className="w-full lg:w-64">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Stato pratica</p>
          <Select value={p.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger data-testid="pratica-status-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{STATI.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid xl:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6 min-w-0">
          <div className="grid md:grid-cols-2 gap-6"><ClienteCard {...ctx} /><ImportiCard {...ctx} /></div>
          <MatchCard {...ctx} />
          <MandatoCard {...ctx} />
          <DocumentiCard {...ctx} />
        </div>
        <div className="space-y-6">
          <RemindersCard {...ctx} />
          <NotesCard {...ctx} />
          <FlagsCard {...ctx} />
          <TimelineCard {...ctx} />
        </div>
      </div>
    </div>
  );
}
